export const REPO_MOCK_SERVER_URL = 'http://repo-mock-server'
export const REPO_MOCK_SERVER_PREFIX = '/__repo_mock_server'

export type RepoMockMode = 'middleware' | 'static'

export interface RepoMockEnv {
  DEV?: boolean
  VITE_MOCK?: string
  VITE_MOCK_MODE?: string
}

export interface RepoMockServiceWorkerStateLike {
  scriptURL?: string
}

export interface RepoMockServiceWorkerRegistrationLike {
  active?: RepoMockServiceWorkerStateLike | null
  installing?: RepoMockServiceWorkerStateLike | null
  waiting?: RepoMockServiceWorkerStateLike | null
  unregister: () => Promise<boolean>
}

export interface RepoMockServiceWorkerContainerLike {
  getRegistrations: () => Promise<ReadonlyArray<RepoMockServiceWorkerRegistrationLike>>
}

export interface RewriteRepoMockRequestUrlOptions {
  baseApi?: string
  mockUrl?: string
  url?: string
}

export function normalizeRepoMockMode(mode?: string): RepoMockMode {
  return mode === 'static' ? 'static' : 'middleware'
}

export function isRepoMockEnabled(env: RepoMockEnv): boolean {
  return Boolean(env.DEV) || env.VITE_MOCK === 'true'
}

export function isRepoMockStaticMode(env: RepoMockEnv): boolean {
  return env.VITE_MOCK === 'true' && normalizeRepoMockMode(env.VITE_MOCK_MODE) === 'static'
}

function isSameOriginRepoMockWorkerScript(scriptURL: string | undefined, origin: string): boolean {
  if (!scriptURL) {
    return false
  }

  try {
    const workerUrl = new URL(scriptURL)
    return workerUrl.origin === origin && workerUrl.pathname === '/mockServiceWorker.js'
  } catch {
    return false
  }
}

function isRepoMockServiceWorkerRegistration(
  registration: RepoMockServiceWorkerRegistrationLike,
  origin: string,
): boolean {
  return [
    registration.active?.scriptURL,
    registration.installing?.scriptURL,
    registration.waiting?.scriptURL,
  ].some(scriptURL => isSameOriginRepoMockWorkerScript(scriptURL, origin))
}

export async function unregisterRepoMockServiceWorkers(
  serviceWorkerContainer?: RepoMockServiceWorkerContainerLike,
  origin?: string,
): Promise<number> {
  const container = serviceWorkerContainer ?? (typeof navigator === 'undefined' ? undefined : navigator.serviceWorker)
  const currentOrigin = origin ?? (typeof window === 'undefined' ? undefined : window.location.origin)
  if (!container || !currentOrigin) {
    return 0
  }

  const registrations = await container.getRegistrations()
  const repoMockRegistrations = registrations.filter(registration =>
    isRepoMockServiceWorkerRegistration(registration, currentOrigin),
  )
  const unregisterResults = await Promise.all(
    repoMockRegistrations.map(registration => registration.unregister()),
  )

  return unregisterResults.filter(Boolean).length
}

export function rewriteRepoMockRequestUrl(options: RewriteRepoMockRequestUrlOptions): string | undefined {
  if (options.mockUrl !== REPO_MOCK_SERVER_URL) {
    return undefined
  }

  const baseApi = options.baseApi ?? ''
  const mockEndpoint = options.url
    ?.replace(baseApi, '')
    ?.replace(REPO_MOCK_SERVER_URL, '') ?? ''
  const mockEndpointPath = mockEndpoint.startsWith('/') ? mockEndpoint : `/${mockEndpoint}`

  return `${REPO_MOCK_SERVER_PREFIX}${mockEndpointPath}`
}
